import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const ACTIONS_TAKEN = ["Drafted Email", "Sent Email", "Phone Call Made", "Walk-in Visit", "Follow-up Sent", "Other"];
export const OFFERS_GIVEN = ["Free Samples", "Coupons", "Catering Menu", "Teacher Appreciation", "Healthcare Hero Coupons", "Other"];

export function useActivities(storeCode) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivities = useCallback(async () => {
    try {
      const { data } = await api.get("/activities", { params: storeCode ? { store_code: storeCode } : {} });
      setActivities(data);
    } catch {
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  }, [storeCode]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const err = (e, m) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || m);

  const addActivity = async (activity) => {
    try {
      const { data } = await api.post("/activities", { ...activity, store_code: storeCode || activity.store_code || null });
      setActivities((p) => [data, ...p]);
      toast.success(data.status === "Completed" ? "Activity logged" : "Activity scheduled");
      return data;
    } catch (e) { err(e, "Failed to add activity"); throw e; }
  };

  const updateActivity = async (id, updates) => {
    try {
      const existing = activities.find((a) => a.id === id) || {};
      const payload = { ...existing, ...updates };
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.date;
      const { data } = await api.put(`/activities/${id}`, payload);
      setActivities((p) => p.map((a) => (a.id === id ? data : a)));
      return data;
    } catch (e) { err(e, "Failed to update activity"); throw e; }
  };

  const completeActivity = async (id, completionNotes) => {
    try {
      const { data } = await api.post(`/activities/${id}/complete`, { completion_notes: completionNotes });
      setActivities((p) => p.map((a) => (a.id === id ? data : a)));
      toast.success("Activity marked as completed");
      return data;
    } catch (e) { err(e, "Failed to complete activity"); throw e; }
  };

  const deleteActivity = async (id) => {
    try {
      await api.delete(`/activities/${id}`);
      setActivities((p) => p.filter((a) => a.id !== id));
      toast.success("Activity deleted");
    } catch (e) { err(e, "Failed to delete activity"); throw e; }
  };

  return { activities, loading, addActivity, updateActivity, completeActivity, deleteActivity, refetch: fetchActivities };
}
